// SSO กับ IAMService — identity provider กลางขององค์กร (skill: iam-authentication)
//
// ผู้ใช้ login ครั้งเดียวที่ IAM แล้วทุกระบบในเครือได้ token ต่อโดยไม่ต้องถามซ้ำ
// IAM ส่ง token กลับมาใน **fragment** ของ URL (`#access_token=…`) ไม่ใช่ query
// เพื่อให้ token ไม่ไปโผล่ใน log ของ web server
//
// ข้อควรระวังทุกข้อในไฟล์นี้มาจาก checklist ของ skill — แต่ละข้อกันปัญหาจริง
// ที่เจอมาแล้วในแอปอื่น อย่าตัดออกเพราะดูเหมือนไม่จำเป็น
import { setSession, type WazzupSession } from "./session";
import { decodeClaims, rolesFromClaims, str } from "./token-claims";

// re-export ไว้เพราะที่อื่นเคย import จากไฟล์นี้
export { rolesFromClaims };

/** ตั้งค่าได้ผ่าน env ตอน build เผื่อชี้ไป IAM ตัวทดสอบ */
const IAM_BASE = import.meta.env.VITE_IAM_BASE ?? "https://iam.fareastfamelineddb.com";

/** state ใช้ครั้งเดียวต่อแท็บ — sessionStorage ไม่ใช่ localStorage */
const STATE_KEY = "iam_sso_state";

/** หน้าที่ผู้ใช้ตั้งใจเข้าก่อนถูกพาไป IAM — returnUrl ต้องเป็นรากเสมอ จึงฝากไว้ที่นี่แทน */
const NEXT_KEY = "iam_sso_next";

/**
 * เคยลองเข้าเงียบ ๆ ในแท็บนี้แล้วหรือยัง
 *
 * มีไว้กันวน: ถ้า IAM ส่งกลับมาแบบไม่มี token (หรือ token ใช้ไม่ได้) แล้วเราลองใหม่
 * อัตโนมัติอีก จะเด้งไป-กลับไม่จบ · ตั้งค่านี้ก่อนออกเดินทางเสมอ
 */
const AUTO_KEY = "iam_sso_auto";

/** ผลของการกลับมาจาก IAM ให้หน้า Staff เอาไปแสดง */
export type SsoOutcome =
  { kind: "none" } | { kind: "ok"; session: WazzupSession } | { kind: "error"; message: string };

const MS_NAME = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name";

/**
 * เริ่ม login: พา browser ไปที่หน้า login ของ IAM (ต้องเป็น full redirect ไม่ใช่ fetch)
 *
 * ผู้ที่ login ที่ระบบอื่นในเครือไว้แล้วจะไม่เห็นหน้ากรอกรหัสของ IAM เลย —
 * IAM เห็น cookie ของตัวเองแล้วเด้งกลับมาพร้อม token ทันที นั่นคือ "ทะลุเข้าไปได้เลย"
 *
 * next = หน้าที่ตั้งใจเข้า · ส่งทาง returnUrl ไม่ได้เพราะ IAM รับเฉพาะ URL ที่ลงทะเบียนไว้
 * (ราก) จึงฝากใน sessionStorage แล้วมาอ่านคืนตอน callback
 */
export function startSsoLogin(next?: string): void {
  const state = crypto.randomUUID();
  sessionStorage.setItem(STATE_KEY, state);
  // ตั้งธงก่อนออกเดินทางเสมอ — ถ้า IAM เด้งกลับมาแบบไม่มี token แล้วเราลองใหม่เอง
  // จะเด้งไป-กลับไม่จบ · ธงถูกล้างเมื่อ callback สำเร็จเท่านั้น
  blockSsoAuto();
  if (next && next !== "/") sessionStorage.setItem(NEXT_KEY, next);
  else sessionStorage.removeItem(NEXT_KEY);
  // returnUrl = รากของแอป — โค้ดตอนเปิดหน้าเป็นคนอ่าน fragment เอง ไม่ต้องมี route แยก
  const returnUrl = `${location.origin}/`;
  const qs = new URLSearchParams({ returnUrl, state });
  location.href = `${IAM_BASE}/api/sso/authorize?${qs}`;
}

/** ปิดการเข้าเงียบ ๆ ของแท็บนี้ — ใช้ตอน logout ไม่งั้นกดออกแล้วถูกพากลับเข้าทันที */
export function blockSsoAuto(): void {
  try {
    sessionStorage.setItem(AUTO_KEY, "1");
  } catch {
    /* storage ใช้ไม่ได้ = ไม่เข้าเงียบ ๆ อยู่แล้ว */
  }
}

/** เคยลองเข้าเงียบ ๆ ในแท็บนี้แล้วหรือยัง */
export function ssoAutoTried(): boolean {
  try {
    return sessionStorage.getItem(AUTO_KEY) === "1";
  } catch {
    return true;
  }
}

/**
 * ออกจากระบบ: ล้างของฝั่งเราให้เสร็จก่อน แล้วค่อยไปล้าง cookie ของ IAM
 * ถ้าล้างแต่ฝั่งเรา ครั้งต่อไปที่กด login จะเข้าเงียบ ๆ ทันทีเพราะ cookie ของ IAM ยังอยู่
 */
export function ssoLogout(clearLocal: () => void): void {
  clearLocal();
  blockSsoAuto();
  const qs = new URLSearchParams({ returnUrl: `${location.origin}/` });
  location.href = `${IAM_BASE}/api/sso/logout?${qs}`;
}

/** กันทำงานซ้ำ — main.tsx อาจถูกเรียกซ้ำได้ในบาง dev flow */
let consumed = false;

/**
 * ผลของ callback ล่าสุด — main.tsx กินไปก่อน render จึงต้องฝากไว้ที่นี่
 * ให้หน้า Staff อ่านไปแสดงเหตุผลตอนล้มเหลว (ห้าม redirect กลับไป authorize เอง
 * ไม่งั้นถ้า error ค้างจะวนไม่จบ — ต้องให้ผู้ใช้กดเริ่มใหม่)
 */
let lastOutcome: SsoOutcome = { kind: "none" };

export function setSsoOutcome(outcome: SsoOutcome): void {
  lastOutcome = outcome;
}

export function getSsoOutcome(): SsoOutcome {
  return lastOutcome;
}

/**
 * แปลง token เป็น session — ใช้ทั้งตอนกลับจาก IAM และตอนเช็คตัวตนเงียบ ๆ
 * คืน "unreadable" / "expired" เพื่อให้ผู้เรียกเลือกข้อความเองได้
 */
function sessionFromToken(token: string): WazzupSession | "unreadable" | "expired" {
  const claims = token ? decodeClaims(token) : null;
  if (!claims) return "unreadable";
  const exp = typeof claims.exp === "number" ? claims.exp : 0;
  if (!exp || exp * 1000 <= Date.now()) return "expired";
  // fragment มีแค่ตัวตน ไม่มีชื่อ/แผนก/รูป — เอาที่อ่านได้จาก claim ไปก่อน
  // useSessionProfile จะดึงโปรไฟล์เต็มด้วย token นี้เองภายหลัง
  return {
    access_token: token,
    expiration: new Date(exp * 1000).toISOString(),
    empCode: str(claims.empCode),
    empEngName: str(claims.name) ?? str(claims[MS_NAME]),
    nickName: str(claims.empCode),
    via: "sso",
    roles: rolesFromClaims(claims),
  };
}

/** ตัวชี้คนของ token — ใช้เทียบว่าคนที่ IAM รู้จักตอนนี้เป็นคนเดียวกับ session ที่ถืออยู่ไหม */
function whoIs(s: WazzupSession | null): string {
  return (s?.empCode || s?.nickName || s?.empEngName || "").trim().toLowerCase();
}

/** หน้าเปล่าที่รับ callback ของการเช็คเงียบ ๆ (client/public/sso-silent.html) */
const SILENT_PATH = "/sso-silent.html";
const SILENT_TIMEOUT_MS = 8000;

/**
 * ถาม IAM เงียบ ๆ ว่า "ตอนนี้เบราว์เซอร์นี้เป็นใคร" แล้วสลับ session ถ้าไม่ใช่คนเดิม
 *
 * ทำไมต้องมี: token ที่เราถือยังไม่หมดอายุ ก็จริง แต่ผู้ใช้อาจไปสลับบัญชีที่ระบบอื่น
 * ในเครือมาแล้ว (เช่นเปิดจาก job.fareastfamelineddb.com ด้วยอีกบัญชี) แท็บนี้จะยัง
 * แสดงข้อมูลของคนเดิมอยู่ทั้งที่ IAM รู้จักคนใหม่แล้ว
 *
 * ทำใน iframe ที่ซ่อนไว้ ไม่ใช่ redirect ทั้งหน้า เพราะการเช็คทุกครั้งที่เปิดหน้า
 * ด้วย redirect แปลว่าทุกการกด F5 ต้องวิ่งผ่าน IAM และ IAM ล่มเมื่อไรแอปใช้ไม่ได้ทันที
 * แบบนี้ถ้าเช็คไม่สำเร็จ (IAM ล่ม/ช้า/ไม่มี cookie) จะไม่ทำอะไรเลย ของเดิมใช้ต่อได้
 *
 * เปลี่ยน session เฉพาะเมื่อได้คำตอบชัดว่าเป็น "คนละคน" เท่านั้น — ไม่เดาจากความเงียบ
 */
export async function checkSsoIdentity(
  current: WazzupSession | null,
): Promise<WazzupSession | null> {
  if (typeof document === "undefined" || !current) return null;
  const state = crypto.randomUUID();
  const qs = new URLSearchParams({ returnUrl: `${location.origin}${SILENT_PATH}`, state });

  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:absolute;width:0;height:0;border:0;visibility:hidden";
  frame.src = `${IAM_BASE}/api/sso/authorize?${qs}`;

  const token = await new Promise<string>((resolve) => {
    const done = (v: string) => {
      window.removeEventListener("message", onMessage);
      clearTimeout(timer);
      frame.remove();
      resolve(v);
    };
    const onMessage = (e: MessageEvent) => {
      // ต้องมาจากหน้าของเราเองเท่านั้น และ state ต้องตรงกับที่เพิ่งสร้าง
      if (e.origin !== location.origin) return;
      const d = e.data as { source?: string; token?: string; state?: string } | null;
      if (!d || d.source !== "iam-sso-silent" || d.state !== state) return;
      done(d.token || "");
    };
    // ไม่มี cookie ที่ IAM = เขาพาไปหน้า login ของเขาใน iframe แล้วเงียบ — ต้องมีเวลาตัด
    const timer = setTimeout(() => done(""), SILENT_TIMEOUT_MS);
    window.addEventListener("message", onMessage);
    document.body.appendChild(frame);
  });

  if (!token) return null;
  const next = sessionFromToken(token);
  if (typeof next === "string") return null;
  if (whoIs(next) && whoIs(next) === whoIs(current)) return null; // คนเดิม ไม่ต้องทำอะไร

  setSession(next);
  return next;
}

/**
 * อ่าน token จาก fragment ตอนกลับมาจาก IAM แล้วเก็บเป็น session
 * เรียกให้เร็วที่สุดตอนเปิดหน้า (ก่อน render) เพื่อไม่ให้มี request ไหนออกไปโดยไม่มี token
 */
export function consumeSsoCallback(): SsoOutcome {
  if (consumed) return { kind: "none" };
  const hash = location.hash.startsWith("#") ? location.hash.slice(1) : "";
  if (!hash.includes("access_token=")) return { kind: "none" };
  consumed = true;

  const params = new URLSearchParams(hash);
  const token = params.get("access_token") ?? "";
  const state = params.get("state") ?? "";

  // state ใช้ครั้งเดียว — ลบก่อนเทียบ เพื่อให้ callback ที่ถูกเปิดซ้ำไม่ผ่าน
  const expected = sessionStorage.getItem(STATE_KEY);
  sessionStorage.removeItem(STATE_KEY);

  // ตัด fragment ออกจาก address bar ก่อนตรวจอะไรทั้งสิ้น — token ต้องไม่ค้างใน history
  // ไม่ว่าจะไปทางไหนต่อ
  history.replaceState(null, "", location.pathname + location.search);

  if (!expected || state !== expected) {
    return {
      kind: "error",
      message: "ลิงก์เข้าสู่ระบบไม่ตรงกับที่เริ่มไว้ — กรุณาเข้าสู่ระบบอีกครั้ง",
    };
  }

  const built = sessionFromToken(token);
  if (built === "unreadable") return { kind: "error", message: "token ที่ได้จาก IAM อ่านไม่ได้" };
  if (built === "expired")
    return { kind: "error", message: "token หมดอายุแล้ว — กรุณาเข้าสู่ระบบอีกครั้ง" };

  const session = built;
  setSession(session);
  // เข้าได้แล้ว — เปิดทางให้ลองเข้าเงียบ ๆ อีกครั้งถ้า session หมดอายุระหว่างใช้งาน
  try {
    sessionStorage.removeItem(AUTO_KEY);
  } catch {
    /* ไม่เป็นไร */
  }
  // พากลับไปหน้าที่ตั้งใจเข้าก่อนถูกส่งไป IAM (replace ไม่ push — ปุ่ม back ต้องไม่ย้อนมาที่ callback)
  const next = sessionStorage.getItem(NEXT_KEY);
  sessionStorage.removeItem(NEXT_KEY);
  if (next && next.startsWith("/") && !next.startsWith("//")) {
    history.replaceState(null, "", next);
  }
  return { kind: "ok", session };
}
